import type { EffectRecipe } from "@lorefoil/web-renderer";

const configuredBase = import.meta.env.VITE_API_BASE_URL as string | undefined;
export const apiBaseURL = configuredBase === undefined ? "" : trustedAPIBaseURL(configuredBase);
const maximumResponseBytes = 8 * 1_024 * 1_024;

export class APIError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  if (apiBaseURL === undefined) throw new APIError("The admin API is not configured safely.", 503);
  if (!isValidAPIPath(path)) throw new APIError("The admin request path is invalid.", 400);
  const method = (init?.method ?? "GET").toUpperCase();
  if (!['GET', 'POST'].includes(method)) throw new APIError("The admin request method is invalid.", 405);
  const endpoint = adminEndpoint(path);
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 30_000);
  try {
    const response = await fetch(endpoint, {
      ...init,
      method,
      credentials: "include",
      headers: { Accept: "application/json", ...init?.headers },
      redirect: "error",
      signal: controller.signal,
    });
    const body = await readBoundedJSON(response);
    if (!response.ok) {
      throw new APIError(errorMessage(body), response.status);
    }
    return body as T;
  } catch (error) {
    if (error instanceof APIError) throw error;
    throw new APIError("The admin request could not be completed.", 503);
  } finally {
    window.clearTimeout(timeout);
  }
}

export function mutationHeaders(): HeadersInit {
  return { "Content-Type": "application/json", "Idempotency-Key": crypto.randomUUID() };
}

export interface Overview {
  users: number;
  cards: number;
  activeGenerations: number;
  failedGenerations24h: number;
  openSupportRequests: number;
  purchaseIssues: number;
  providerCost24h: { amount: number; unit: string };
  providerCosts24h?: Array<{ provider: string; unit: string; amount: number | null; unpricedRequests: number }>;
  asOf: string;
}

export interface Generation {
  id: string;
  ownerId: string;
  status: string;
  billingStatus: string;
  quotedCost: number;
  errorCode: string | null;
  provider: string | null;
  providerState: string | null;
  providerCost: { amount: number; unit: string | null } | null;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface PurchaseEvent {
  eventId: string;
  ownerId: string | null;
  productId: string;
  type: string;
  status: string;
  errorCode: string | null;
  occurredAt: string;
  receivedAt: string;
}

export interface SharedLinkRecord {
  id: string;
  ownerId: string;
  cardId: string;
  cardTitle: string;
  status: "active" | "revoked";
  createdAt: string;
  revokedAt: string | null;
}

export interface AuditEvent {
  id: string;
  actorEmail: string;
  action: string;
  targetType: string;
  targetId: string;
  reason: string;
  requestId: string;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface EffectAssetDescriptor {
  assetId: string;
  role: string;
  mimeType: "image/jpeg" | "image/png" | "image/webp";
  pixelWidth: number;
  pixelHeight: number;
  byteSize: number;
  sha256: string;
  alphaMode: "opaque" | "straight" | "mask";
}

export interface EffectLabManifest {
  schemaVersion: 2;
  rendererVersion: string;
  templateVersion: string;
  render: {
    finish: "standard" | "goldFoil" | "holographic";
    intensity: number;
    roughness: number;
    reflection: number;
    parallax: number;
    hueShift: number;
    flipAxis: "vertical";
  };
  front: {
    base: EffectAssetDescriptor;
    preview: EffectAssetDescriptor;
    background: EffectAssetDescriptor;
    subject: EffectAssetDescriptor;
    lineart: EffectAssetDescriptor;
    typography: EffectAssetDescriptor;
    foilMask?: EffectAssetDescriptor;
    exclusionMask?: EffectAssetDescriptor;
    particleAtlas?: EffectAssetDescriptor;
  };
  back: { base: EffectAssetDescriptor; preview: EffectAssetDescriptor };
  effectRecipe: EffectRecipe;
}

export interface EffectLabRecord {
  card: { id: string; title: string; version: number };
  revisionId: string;
  manifest: EffectLabManifest;
  warnings: string[];
  history: Array<{
    id: string;
    sequence: number;
    createdAt: string;
    isCurrent: boolean;
    recipe: EffectRecipe;
  }>;
}

export interface EffectCardSummary {
  id: string;
  title: string;
  version: number;
  updatedAt: string;
  rendererVersion: string;
  revisionSequence: number;
}

export async function loadEffectAssetURL(
  cardId: string,
  descriptor: EffectAssetDescriptor,
  signal: AbortSignal,
): Promise<string> {
  if (!isUUID(cardId) || !isUUID(descriptor.assetId)
      || descriptor.byteSize < 1 || descriptor.byteSize > 32 * 1_024 * 1_024) {
    throw new APIError("The Effect Lab asset descriptor is invalid.", 400);
  }
  const response = await fetch(adminEndpoint(`/cards/${cardId}/assets/${descriptor.assetId}`), {
    method: "GET",
    headers: { Accept: descriptor.mimeType },
    credentials: "include",
    redirect: "error",
    signal,
  });
  if (!response.ok || response.headers.get("Content-Type")?.split(";", 1)[0]?.trim().toLowerCase() !== descriptor.mimeType) {
    try { await response.body?.cancel(); } catch { /* best effort */ }
    throw new APIError("An Effect Lab asset could not be loaded.", response.status || 502);
  }
  const bytes = await readExactBytes(response, descriptor.byteSize, signal);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  if (hexDigest(digest) !== descriptor.sha256) throw new APIError("An Effect Lab asset failed its integrity check.", 502);
  return URL.createObjectURL(new Blob([bytes], { type: descriptor.mimeType }));
}

function adminEndpoint(path: string): string {
  return apiBaseURL ? `${apiBaseURL}/v1/admin${path}` : `/api${path}`;
}

async function readExactBytes(response: Response, expectedBytes: number, signal: AbortSignal): Promise<ArrayBuffer> {
  const declared = response.headers.get("Content-Length");
  if (declared !== null && (!/^(0|[1-9][0-9]*)$/u.test(declared) || Number(declared) !== expectedBytes)) {
    throw new APIError("An Effect Lab asset has an invalid size.", 502);
  }
  if (!response.body) throw new APIError("An Effect Lab asset is empty.", 502);
  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  try {
    while (true) {
      if (signal.aborted) throw new DOMException("Aborted", "AbortError");
      const { done, value } = await reader.read();
      if (done) break;
      if (!value) continue;
      total += value.byteLength;
      if (total > expectedBytes) throw new APIError("An Effect Lab asset is oversized.", 502);
      chunks.push(value);
    }
  } catch (error) {
    try { await reader.cancel(); } catch { /* best effort */ }
    throw error;
  } finally { reader.releaseLock(); }
  if (total !== expectedBytes) throw new APIError("An Effect Lab asset is incomplete.", 502);
  const bytes = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    bytes.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return bytes.buffer;
}

function hexDigest(value: ArrayBuffer): string {
  return [...new Uint8Array(value)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

function isUUID(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu.test(value);
}

function trustedAPIBaseURL(value: string): string | undefined {
  if (value.length === 0 || value.length > 2_048 || value !== value.trim()) return undefined;
  try {
    const url = new URL(value);
    if (url.protocol !== "https:"
        || !["api.lorefoil.kifferliu.com", "staging-api.lorefoil.kifferliu.com"].includes(url.hostname)
        || url.port !== "" || url.username !== "" || url.password !== ""
        || url.pathname !== "/" || url.search !== "" || url.hash !== "") return undefined;
    return url.origin;
  } catch { return undefined; }
}

function isValidAPIPath(value: string): boolean {
  return value.length >= 1 && value.length <= 4_096
    && value.startsWith("/") && !value.startsWith("//")
    && !value.includes("\\") && !value.includes("#")
    && !/[\u0000-\u001f\u007f]/u.test(value);
}

async function readBoundedJSON(response: Response): Promise<unknown> {
  const contentType = response.headers.get("Content-Type")?.toLowerCase() ?? "";
  if (!contentType.startsWith("application/json")) throw new APIError("The admin API returned an invalid response.", 502);
  const declaredLength = response.headers.get("Content-Length");
  if (declaredLength !== null) {
    if (!/^(0|[1-9][0-9]*)$/u.test(declaredLength)) throw new APIError("The admin API returned an invalid response.", 502);
    const parsedLength = Number(declaredLength);
    if (!Number.isSafeInteger(parsedLength) || parsedLength > maximumResponseBytes) {
      throw new APIError("The admin API returned an oversized response.", 502);
    }
  }
  if (!response.body) throw new APIError("The admin API returned an invalid response.", 502);
  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  try {
    while (true) {
      const result = await reader.read();
      if (result.done) break;
      total += result.value.byteLength;
      if (total > maximumResponseBytes) throw new APIError("The admin API returned an oversized response.", 502);
      chunks.push(result.value);
    }
  } catch (error) {
    try { await reader.cancel(); } catch { /* best effort */ }
    throw error;
  }
  const bytes = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    bytes.set(chunk, offset);
    offset += chunk.byteLength;
  }
  try {
    return JSON.parse(new TextDecoder("utf-8", { fatal: true }).decode(bytes)) as unknown;
  } catch {
    throw new APIError("The admin API returned invalid JSON.", 502);
  }
}

function errorMessage(value: unknown): string {
  if (!value || typeof value !== "object" || Array.isArray(value)) return "The admin request could not be completed.";
  const body = value as Record<string, unknown>;
  const nested = body.error && typeof body.error === "object" && !Array.isArray(body.error)
    ? body.error as Record<string, unknown>
    : undefined;
  const candidate = typeof body.message === "string" ? body.message
    : typeof nested?.message === "string" ? nested.message
      : undefined;
  const normalized = candidate?.trim();
  return normalized ? normalized.slice(0, 512) : "The admin request could not be completed.";
}
